/**
 * What a product is — the profile of its type on the product-type tree, as the
 * api resolves it on every product it sends: { id, name, color, kind, page
 * ("book" | "album" | "store"), player, library, physical, download, license,
 * bookPreview, bookLike, accent }. Null for one sent without it.
 */
export const profileOf = (product) => product?.profile || null

export function isPhysicalProduct(product) {
  const profile = profileOf(product)
  if (profile) return !!profile.physical
  if (product?.isPhysicalProduct !== undefined) return product.isPhysicalProduct
  if (product?.contentTypeData?.isPhysical !== undefined) return product.contentTypeData.isPhysical
  return false
}

/** Sells keys (a licence): its profile says so */
export const isLicenseProduct = (product) => !!profileOf(product)?.license

// "EUR_1200" -> { currency: "eur", amount: 1200 }
export function parseRealPrice(realPrice) {
  const match = realPrice?.match?.(/^([A-Z]+)_(\d+)$/)
  if (!match) return null
  return { currency: match[1].toLowerCase(), amount: parseInt(match[2], 10) }
}

/**
 * The money price a product is priced at (cents + currency): `real_price`
 * ("EUR_1234"), else the base the api answered, else the api's display price.
 * What the visitor sees is this converted into the chosen currency
 * (store/currency `convert`).
 */
export function basePrice(product) {
  const parsed = parseRealPrice(product?.real_price)
  if (parsed) return parsed
  if (product?.real_money_cost_base && product?.real_money_currency_base) return { amount: product.real_money_cost_base, currency: String(product.real_money_currency_base).toLowerCase() }
  if (product?.real_money_cost > 0 && product?.real_money_currency) return { amount: product.real_money_cost, currency: String(product.real_money_currency).toLowerCase() }
  return null
}

// Fill real_money_cost/currency from real_price when missing (cart normalisation)
export function normalizeProduct(product) {
  if (!product) return product
  if (product.real_money_cost || !product.real_price) return product
  const parsed = parseRealPrice(product.real_price)
  if (!parsed) return product
  return { ...product, real_money_cost: parsed.amount, real_money_currency: parsed.currency }
}

/**
 * Whether the product is sold in coins: its primary price model is the virtual
 * one. The server answers that as `price_type`; an older payload carries none,
 * and there the money price wins whenever there is one.
 *
 * `allowCreditsPurchase` is not the question — since the catalog fold it only
 * says coins are accepted as well, and every migrated product has a coin model.
 */
export function isSoldInCoins(product) {
  if (!product) return false
  if (product.price_type) return product.price_type === "virtual"
  const hasRealMoney = !!(product.real_price || product.real_money_cost)
  return !hasRealMoney && product.allowCreditsPurchase !== false
}

// the publish workflow of a catalog product (API db/schemas/product.js):
// only an approved one has a page
const PUBLISHED = 2

/**
 * Whether the store offers this product at all: `enabled` is the one switch in
 * the admin, and the old payloads answer the same thing as
 * `availableForPurchase` (derived from it on the server). A catalog product is
 * offered only once it is approved — a draft, one waiting for review and a
 * rejected one all read as not available.
 *
 * A store product that is not a catalog product (a coins pack, a gift card)
 * carries no `publishStatus`, and nothing is asked of it.
 */
export function isProductOffered(product) {
  if (!product) return false
  if (product.enabled === false) return false
  if (product.publishStatus !== undefined && product.publishStatus !== PUBLISHED) return false
  return product.availableForPurchase !== false
}

/**
 * What a buyer's pick is judged sold out by: the chosen variant (store product
 * extensions: options) when the selection line names one the product lists,
 * else the product itself — whose `in_stock` / `stock`, for a product with
 * variants or an outfit, the server already derives from its variants / parts
 * (publicFields, list fields).
 */
export function stockSourceOf(product, line) {
  const variant = line?.sku && Array.isArray(product?.variants) ? product.variants.find((entry) => entry?.sku === line.sku) : null
  if (!variant) return product
  // a product out of stock as a whole (every variant gone, a fresher answer than the page's variants) stays so
  return { ...product, in_stock: product.in_stock !== false && variant.enabled !== false && variant.inStock !== false, stock: Number.isFinite(variant.stock) ? variant.stock : null }
}

/**
 * How many of a product one order may hold (its own limit, else its seller's — the server
 * answers `maxPerOrder` as it applies), or null for no limit.
 */
export const orderLimitOf = (product) => (Number(product?.maxPerOrder) > 0 ? Number(product.maxPerOrder) : null)

/** Whether `adding` more would go over the product's limit, with `inCart` of it in the cart already. */
export const overOrderLimit = (product, inCart = 0, adding = 1) => {
  const limit = orderLimitOf(product)
  return limit !== null && inCart + adding > limit
}

export function isOutOfStock(product, quantityInCart = 0) {
  if (!product) return false
  if (product.availableForPurchase === false) return true
  // the framework's availability resolvers already answered for this product
  // ("Out of Stock" marks it by hand, license keys count their free keys), and
  // that answer outranks the stock number the product carries
  if (product.in_stock === false) return true
  if (product.stock === null || product.stock === undefined) return false
  return product.stock <= quantityInCart
}

// Old store pages show the part after "CD:" / "Book:" prefixes as the display title
// The store text of a hand-made product may carry its kind as a prefix
// ("Book: Дързост", "CD: Etudes"); the page shows the name alone. Only a
// known kind is a prefix: a colon inside a real title ("Murdle: кримиграми",
// "Класически самолети: хартиени модели") is part of the title and stays.
const KINDS = ["book", "cd", "album", "audiobook", "audio book", "soap", "product", "книга", "албум", "сапун", "аудиокнига"]
export function displayName(product) {
  const name = product?.name || ""
  const colon = name.indexOf(":")
  if (colon < 0) return name
  const prefix = name.slice(0, colon).trim().toLowerCase()
  return KINDS.includes(prefix) ? name.slice(colon + 1).trim() : name
}

export const formatNumber = (num) => {
  if (!num) return "0"
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
}

export const formatDate = (dateString) => {
  if (!dateString) return ""
  const date = new Date(dateString)
  const dd = String(date.getDate()).padStart(2, "0")
  const mm = String(date.getMonth() + 1).padStart(2, "0")
  return `${dd} / ${mm} / ${date.getFullYear()}`
}

// YouTube id from any youtube/youtu.be URL
export const getYoutubeId = (url) => {
  if (!url) return null
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([^&?\s/]+)/)
  return match ? match[1] : null
}

/**
 * Per-platform instructions HTML of a license product (old InstructionsHtml):
 * `{{store}}` becomes a store-link chip, `{{platformName}}` the platform name.
 */
export function renderInstructions(entry, language = "en") {
  if (!entry) return ""
  const p = entry.platform || entry
  let html = typeof entry.instructions === "string" ? entry.instructions : entry.instructions?.[language] || entry.instructions?.en || ""
  if (!html) return ""
  if (html.includes("{{store}}")) {
    const img = p.image ? `<img src="${p.image}" alt="${p.name}" style="height:14px;width:auto;vertical-align:middle;">` : ""
    const chip = entry.storeLink
      ? `<a href="${entry.storeLink}" target="_blank" rel="noopener noreferrer" style="display:inline-flex;align-items:center;gap:4px;padding:2px 10px;border-radius:16px;border:1px solid rgba(0,0,0,0.23);font-size:12px;font-weight:500;">${img}${p.name}</a>`
      : `${img}${p.name}`
    html = html.replaceAll("{{store}}", chip)
  }
  return html.replaceAll("{{platformName}}", p.name || "")
}

// what "Open" plays in place (its profile's player), everything else opens its page
export const isPlayerContent = (product) => !!profileOf(product)?.player
/**
 * Whether the reader owns this title: digital content the site opens for
 * them (the api's is_bought, which a licence or a paper book never sets —
 * those are bought again).
 */
export const isOwned = (product) => !!product?.is_bought

/**
 * Bought again rather than opened: a licence (one more key, one more seat) and
 * a physical product (one more copy) are sold however many the buyer has; only
 * what plays or downloads becomes "Open" once owned.
 */
export const isRepeatPurchase = (product) => isLicenseProduct(product) || isPhysicalProduct(product)
